import type { AxiosError, CreateAxiosDefaults } from "axios";
import axios from "axios";
import { useCallback, useEffect, useMemo } from "react";
import { useAuthStore } from "stores/authStore";

const baseURL = process.env.ASSISTIVE_BACKEND_URL;
const url = "/graphql";

// Enhanced error types for better error handling
export type FetchError = {
  type: "network" | "graphql" | "timeout" | "abort" | "auth";
  message: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalError?: any;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface UseFetchProps<T = any> {
  query: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  variables?: Record<string, any>;
  pause?: boolean;
  retry?: number;
  timeout?: number; // Add timeout support
  onSuccess?: (data: T) => void;
  onError?: (error: FetchError) => void;
  setIsLoading?: (isLoading: boolean) => void;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type VariablesArg = Record<string, any>;

// Create enhanced error helper
const createError = (
  type: FetchError["type"],
  message: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  originalError?: any,
): FetchError => ({
  type,
  message,
  originalError,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useFetch = <T = any>({
  query,
  variables,
  pause = false,
  retry = 3,
  timeout = 60000,
  onSuccess,
  onError,
  setIsLoading,
}: UseFetchProps<T>) => {
  // Runtime safety check - educate developers about hook's purpose
  if (!onSuccess) {
    console.warn(
      "useFetch: onSuccess callback is required. This hook implements Client Authoritative State pattern and does not return data directly. Provide onSuccess to handle the fetched data.",
    );
  }

  // Stable serialization of variables - prevents unnecessary refetches
  const variablesKey = useMemo(() => JSON.stringify(variables), [variables]);

  const exeFetch = useCallback(
    (variablesParam?: VariablesArg) => {
      const { cleanup } = fetchData({
        query,
        variables: variablesParam || variables,
        onSuccess,
        onError,
        setIsLoading,
      });

      return { cleanup };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query, variablesKey, retry, timeout],
  );

  useEffect(() => {
    if (pause) {
      // If paused, don't fetch
      return;
    }
    const { cleanup } = exeFetch();
    // Cleanup on unmount
    return () => cleanup();
  }, [pause, exeFetch]);

  // Return only sync-related state, no data
  return exeFetch;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface FetchDataArgs<T = any> {
  retry?: number;
  triedRefresh?: boolean;
  timeout?: number;
  query: string;
  variables?: VariablesArg;
  onSuccess?: (data: T) => void;
  onError?: (error: FetchError) => void;
  setIsLoading?: (isLoading: boolean) => void;
  signal?: AbortSignal;
}

export const fetchData = (args: FetchDataArgs) => {
  const {
    timeout = 60000,
    retry = 3,
    query,
    variables,
    onSuccess,
    onError,
    setIsLoading,
    signal: signalArg,
  } = args;

  const timeoutIds: ReturnType<typeof setTimeout>[] = [];
  const controller = new AbortController();

  const signal = signalArg || controller.signal;

  const exeFetchData = async (
    attempt: number = 0,
    triedRefresh: boolean = false,
  ) => {
    try {
      if (setIsLoading) {
        setIsLoading(true);
      }
      const axiosInstance = axios.create({
        baseURL,
        timeout, // Add timeout support
      } as CreateAxiosDefaults);

      const result = await axiosInstance({
        method: "POST",
        url,
        data: {
          query,
          variables,
        },
        signal,
        withCredentials: true,
      });

      // Pass result to caller regardless of GraphQL errors
      // Caller can inspect result.errors and decide how to handle
      if (result.data.errors) {
        console.warn("GraphQL errors in response", result.data.errors);
      }
      if (onSuccess) {
        onSuccess(result.data);
      }

      // Success - stop loading
      if (
        !signal.aborted &&
        setIsLoading &&
        typeof setIsLoading === "function"
      ) {
        setIsLoading(false);
      }
    } catch (err) {
      if (signal.aborted) {
        return;
      }

      let fetchError: FetchError;
      // --- Detect 403 and try refresh token ---
      if (
        (err as AxiosError).response &&
        (err as AxiosError).response?.status === 403 &&
        !triedRefresh
      ) {
        try {
          await useAuthStore.getState().actions.refresh();
          // Retry original request after refreshing token
          await exeFetchData(attempt, true);
          return;
        } catch (refreshErr) {
          fetchError = createError(
            "auth",
            "Authentication failed after refresh attempt",
            refreshErr,
          );
        }
      } else if (err instanceof Error && "type" in err) {
        fetchError = err as FetchError;
      } else if (
        err instanceof Error &&
        "code" in err &&
        (err as AxiosError).code === "ECONNABORTED"
      ) {
        fetchError = createError("timeout", "Request timeout", err);
      } else if ((err as AxiosError).response) {
        fetchError = createError(
          "network",
          `HTTP ${(err as AxiosError).response?.status}: ${(err as AxiosError).response?.statusText || (err as AxiosError).message}`,
          err,
        );
      } else {
        fetchError = createError("network", "Network error", err);
      }

      if (attempt < retry) {
        // Retry - keep loading state true
        // Cap delay at 5 seconds to prevent excessive wait times
        const delay = Math.min(1000 * (attempt + 1), 5000);
        console.warn(
          `Fetch attempt ${attempt + 1} failed. Retrying in ${delay}ms...`,
          fetchError,
        );
        const timeoutId = setTimeout(
          () => exeFetchData(attempt + 1, triedRefresh),
          delay,
        );
        timeoutIds.push(timeoutId);
      } else {
        // Final failure - stop loading
        console.error(fetchError);
        if (typeof onError === "function") {
          onError(fetchError);
        }
        if (
          !signal.aborted &&
          setIsLoading &&
          typeof setIsLoading === "function"
        ) {
          setIsLoading(false);
        }
      }
    }
  };

  exeFetchData();

  return {
    cleanup: () => {
      if (!signalArg) {
        controller.abort();
      }
      timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    },
  };
};
